<template>
  <div
    @click="handleCardClick"
    @click.middle="handleCardClick"
    @keyup.enter="handleCardEnter"
    :tabindex="cardTabIndex"
    :class="cardClass"
    class="cta-card card"
    :data-background-image="cardBgImage"
    :style="cardStyle">
    <div class="pb-space-xxs card-body">
      <!-- label -->
      <div
        v-if="displayLabel"
        class="mb-space-xs p-space-xxs d-inline-block"
        :class="labelClass">
        {{ labelText }}
      </div>
      <h3 :class="titleClass" class="mb-space-xs">{{ title }}</h3>
      <hr
        v-if="displayUnderline"
        class="title-underline d-inline-block mt-0 mb-space-xs"
        :style="underlineStyle" />
      <!-- optional slot -->
      <slot />
    </div>

    <div
      v-if="displayFooter"
      :class="footerClasses"
      class="bg-transparent p-0 card-footer border-top">
      <a
        :target="ctaLinkTarget"
        :class="ctaClass"
        :href="ctaLink"
        :aria-label="ctaText"
        class="rds-link d-flex align-items-center justify-content-between py-space-sm footer-link"
        @click="handleCtaClick"
        >{{ ctaText }}
        <div class="icon-wrapper">
          <div class="cta-icon-block" v-if="props.displayIconCta">
            <svg
              role="presentation"
              class="cta-icon-class"
              xmlns="http://www.w3.org/2000/svg"
              height="1em"
              viewBox="0 0 320 512">
              <!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
              <path
                d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
            </svg>
          </div>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

interface Props {
  title?: string;
  titleSize?: string;
  titleVariant?: string;
  titleAlign?: "left" | "right" | "center";
  displayFooter?: boolean;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaLink?: string;
  ctaTextWeight?: string;
  ctaLinkTarget?: "_self" | "_blank" | "_parent" | "_top";
  backgroundImageSource?: string;
  bgImageLazy?: boolean;
  isClickable?: boolean;
  displayUnderline?: boolean;
  underlineColor?: string;
  displayCardHover?: boolean;
  hoverColor?: string;
  cardBackgroundVariant?: string;
  displayBorder?: boolean;
  borderVariant?: string;
  displayLabel?: boolean;
  labelBackgroundVariant?: string;
  labelText?: string;
  labelTextSize?: string;
  labelTextVariant?: string;
  labelTextWeight?: string;
  displayIconCta?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Heading text",
  titleSize: "medium",
  titleVariant: "dark-3",
  titleAlign: "left",
  displayFooter: true,
  ctaText: "Text Link",
  ctaTextSize: "medium",
  ctaTextVariant: "primary",
  ctaLink: "https://asuonline.asu.edu/",
  ctaTextWeight: "bold",
  ctaLinkTarget: "_self",
  bgImageLazy: true,
  isClickable: false,
  displayUnderline: true,
  underlineColor: "var( --rds-secondary, #ffc627)",
  displayCardHover: true,
  hoverColor: "var( --rds-secondary, #ffc627)",
  cardBackgroundVariant: "white",
  displayBorder: true,
  borderVariant: "light-4",
  displayLabel: true,
  labelBackgroundVariant: "secondary",
  labelText: "Text highlight",
  labelTextSize: "xs",
  labelTextVariant: "dark-3",
  labelTextWeight: "bold",
  displayIconCta: true,
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when any the enter key is pressed while the card has focus and clickable-card is set to true.
   */
  (e: "cardEnter", event: Event): void;
}>();

const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
};

const cardTabIndex = computed(() => {
  return props.isClickable ? "0" : "-1";
});

const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant} text-${
    ctaAlignMap[props.titleAlign]
  }`;
});

const ctaClass = computed(() => {
  return `text-${props.ctaTextSize} text-${props.ctaTextVariant} fw-${props.ctaTextWeight}`;
});

const underlineStyle = computed(() => {
  return `background-color: ${props.underlineColor};`;
});

const cardStyle = computed(() => {
  let style = "";

  style += `--hover-color: ${props.hoverColor};`;
  props.isClickable ? (style += `cursor: pointer;`) : null;

  if (!props.bgImageLazy && props.backgroundImageSource) {
    style += `background-image:url('${props.backgroundImageSource}')`;
  }

  return style;
});

const cardBgImage = computed(() => {
  return props.bgImageLazy ? props.backgroundImageSource : null;
});

const cardClass = computed(() => {
  let classNames = "";

  props.displayBorder
    ? (classNames += ` border border-${props.borderVariant}`)
    : (classNames += " border-0");

  if (props.bgImageLazy) {
    classNames += " cta-lazy";
  }

  if (props.cardBackgroundVariant) {
    classNames += ` bg-${props.cardBackgroundVariant}`;
  }

  if (props.isClickable) {
    classNames += " card-focus";
  }

  if (props.displayCardHover) {
    classNames += " card-hover-effect";
  }

  return classNames;
});

const labelClass = computed(() => {
  return `bg-${props.labelBackgroundVariant} fs-${props.labelTextSize} text-${props.labelTextVariant} fw-${props.labelTextWeight}`;
});

const footerClasses = computed(() => {
  return `border-${props.borderVariant}`;
});

const handleCtaClick = (event: Event) => {
  emits("ctaClick", event);
};

const handleCardClick = (event: Event) => {
  if (props.isClickable) {
    emits("cardClick", event);
  }
};

const handleCardEnter = (event: Event) => {
  if (props.isClickable) {
    emits("cardEnter", event);
  }
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null && props.bgImageLazy) {
    const observer = lozad(".cta-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.title-underline {
  height: 4px;
  width: 30px;
  border-top: none;
}
.cta-card {
  min-height: 68px;

  &:hover {
    --hover-color: var(--rds-secondary);

    .cta-icon-block {
      background-color: var(--hover-color);
      border: none;
    }
  }
  &:focus {
    outline: 2px solid var(--rds-dark-3);
    box-shadow: none;
  }
  &.bg-dark-3:focus {
    outline-offset: 2px;
  }
}

.card-hover-effect:hover {
  -webkit-box-shadow: inset 0 -8px var(--hover-color);
  box-shadow: inset 0 -8px var(--hover-color);
}

.footer-link {
  padding-left: 20px;
  padding-right: 20px;
}
/*Ensures the footer link keeps a consistent height even when the icon is missing*/
.icon-wrapper {
  --icon-size: 24px;
  min-height: var(--icon-size);
}
.cta-icon-block {
  height: var(--icon-size);
  width: var(--icon-size);
  border-radius: 50%;
  border: 1px solid var(--rds-light-3);
  background-color: var(--rds-light-1);
  display: flex;
  align-items: center;
}
.cta-icon-class {
  height: 12px;
  width: 100%;
  color: var(--rds-dark-3);
}
</style>
